import { updateCouponPool as updateCouponPoolAction } from 'actions/couponPools'
import { alert } from 'actions/flash'
import { updateRedeemTemplate } from 'actions/redeemTemplates'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import useHiddenMenus from 'hooks/useHiddenMenus'
// eslint-disable-next-line no-restricted-imports
import { DebouncedFunc } from 'lodash'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import {
  CouponPoolGenerationType,
  CouponPoolType,
  Fields as CouponPool,
} from 'models/CouponPool'
import { Fields as PointsExperience } from 'models/PointsExperience'
import {
  Fields as RedeemTemplate,
  RedeemTemplateSubtype,
  RedeemType,
  RedemptionVenue,
} from 'models/RedeemTemplate'
import { RedemptionProvider } from 'models/RedemptionProvider'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectExperience } from 'selectors/pointsExperience'
import {
  couldHaveDiscountedProducts,
  getRedeemTemplateType,
  getSavedModifierDiscountIds,
  getUniqueRedemptionProviders,
  isLoyaltyIntegrationPos,
  isNDR,
  shouldHaveRequiredProducts,
} from '../helpers'
import { InstoreRedemptionType } from '../types'
import { DiscountScope, FormModel } from './FormModel'
import useCouponPools from './useCouponPools'

type ProductIdMap = {
  instore: {
    discounted?: string[]
    discounted_related?: string[]
    discounted_modifiers?: string[]
    required?: string[]
  }
  digital: {
    discounted?: string[]
    discounted_related?: string[]
    discounted_modifiers?: string[]
    required?: string[]
  }
}

type PosField = {
  instoreDiscountedProductIds: string[]
  instoreDiscountedModifierIds: string[]
  instoreRequiredProductIds: string[]
  onlineDiscountedProductIds: string[]
  onlineDiscountedModifierIds: string[]
  onlineRequiredProductIds: string[]
  instoreModifierDiscountIds?: string[]
  onlineModifierDiscountIds?: string[]
  instoreModifierDiscountsAdvanced?: boolean
}

type PosFields = Record<string, PosField>

export type SaveFn = (
  model: FormModel,
  selectedProvider?: RedemptionProvider
) => Promise<boolean>
interface Result {
  triggerSave: DebouncedFunc<SaveFn>
  forceSave: SaveFn
  isSaving: boolean
  error: string | null
}

const normalizeProductIds = (
  ids: string[],
  applicable: boolean
): string[] | undefined => {
  if (!applicable) return undefined
  return ids.length ? ids : []
}

const getDiscounts = (
  discounted: string[] = [],
  related: string[] = [],
  required: string[] = [],
  templateType: RedeemType,
  subtype: RedeemTemplateSubtype
): {
  discounted?: string[]
  discounted_related?: string[]
  required?: string[]
} => {
  const discountedProductsApplicable =
    couldHaveDiscountedProducts(templateType, subtype) ||
    (subtype === null && !isEmpty(discounted))
  const requiredProductsApplicable =
    shouldHaveRequiredProducts(templateType, subtype) ||
    (subtype === null && !isEmpty(required))

  return {
    discounted: normalizeProductIds(discounted, discountedProductsApplicable),
    discounted_related: normalizeProductIds(
      related,
      discountedProductsApplicable
    ),
    required: normalizeProductIds(required, requiredProductsApplicable),
  }
}

// Modifier discounts only exist on fixed-price templates, so other types
// leave the key out. Under any other fixed-price scope the list is sent empty
// so a scope change away from modifier discounts clears what was stored.
// Outside advanced mode the list is a copy of the modifier restrictions.
const getModifierDiscounts = (
  ids: string[] = [],
  templateType: RedeemType,
  discountScope: DiscountScope,
  { advanced, restrictionIds }: { advanced: boolean; restrictionIds?: string[] }
): string[] | undefined => {
  if (templateType !== 'manual/fixed_price') return undefined
  return getSavedModifierDiscountIds(ids, discountScope, {
    advanced,
    restrictionIds,
  })
}

export default function (templateId: number | null): Result {
  const [isSaving, setIsSaving] = useState(false)
  const dispatch = useDispatch()
  const templateMinMaxEnabled = useFlag('mx-template-min-max', false)

  const { createCouponPool, getCouponPools } = useCouponPools()
  const pointsExperience: PointsExperience | null =
    useSelector(selectExperience)
  const { categories } = useHiddenMenus()

  const merchant = useCurrentMerchant()
  // Memoize so the array identity is stable across renders — it feeds the
  // debounced save's dependency chain, which would otherwise reset every render.
  const redemptionProviders = useMemo(
    () => getUniqueRedemptionProviders(merchant),
    [merchant]
  )
  const couponPoolType = merchant?.instore_scanning_enabled
    ? CouponPoolType.QRCODE
    : CouponPoolType.RAW

  const buildProductIds = useCallback(
    (
      formModel: FormModel,
      templateType: RedeemType,
      subtype: RedeemTemplateSubtype,
      posFields: PosFields
    ): {
      baseProductIds: FormModel['productIds'] & ProductIdMap
      providerProductIds: Record<string, ProductIdMap>
    } => {
      const isModifier = subtype === 'modifier'
      const { discountScope } = formModel

      const baseProductIds: FormModel['productIds'] & ProductIdMap = {
        ...formModel.productIds,
        instore: {
          ...getDiscounts(
            isModifier
              ? formModel.instoreDiscountedModifierIds
              : formModel.instoreDiscountedProductIds,
            isModifier
              ? formModel.instoreDiscountedProductIds
              : formModel.instoreDiscountedModifierIds,
            formModel.instoreRequiredProductIds,
            templateType,
            subtype
          ),
          discounted_modifiers: getModifierDiscounts(
            formModel.instoreModifierDiscountIds,
            templateType,
            discountScope,
            {
              advanced: formModel.instoreModifierDiscountsAdvanced,
              restrictionIds: formModel.instoreDiscountedModifierIds,
            }
          ),
        },
        digital: {
          ...getDiscounts(
            isModifier
              ? formModel.onlineDiscountedModifierIds
              : formModel.onlineDiscountedProductIds,
            isModifier
              ? formModel.onlineDiscountedProductIds
              : formModel.onlineDiscountedModifierIds,
            formModel.onlineRequiredProductIds,
            templateType,
            subtype
          ),
          discounted_modifiers: getModifierDiscounts(
            formModel.onlineModifierDiscountIds,
            templateType,
            discountScope,
            {
              advanced: formModel.onlineModifierDiscountsAdvanced,
              restrictionIds: formModel.onlineDiscountedModifierIds,
            }
          ),
        },
      }

      const providerProductIds: Record<string, ProductIdMap> = Object.entries(
        posFields
      ).reduce((acc, [providerKey, field]) => {
        const discounted = isModifier
          ? field.instoreDiscountedModifierIds
          : field.instoreDiscountedProductIds
        const related = isModifier
          ? field.instoreDiscountedProductIds
          : field.instoreDiscountedModifierIds

        acc[providerKey] = {
          instore: {
            ...getDiscounts(
              discounted,
              related,
              field.instoreRequiredProductIds,
              templateType,
              subtype
            ),
            discounted_modifiers: getModifierDiscounts(
              field.instoreModifierDiscountIds,
              templateType,
              discountScope,
              {
                advanced: Boolean(field.instoreModifierDiscountsAdvanced),
                restrictionIds: field.instoreDiscountedModifierIds,
              }
            ),
          },
          digital: {
            ...getDiscounts(
              isModifier
                ? field.onlineDiscountedModifierIds
                : field.onlineDiscountedProductIds,
              isModifier
                ? field.onlineDiscountedProductIds
                : field.onlineDiscountedModifierIds,
              field.onlineRequiredProductIds,
              templateType,
              subtype
            ),
            // Provider digital copies are never edited or priced, so they
            // are sent as stored
            discounted_modifiers: getModifierDiscounts(
              field.onlineModifierDiscountIds,
              templateType,
              discountScope,
              { advanced: true }
            ),
          },
        }

        return acc
      }, {} as Record<string, ProductIdMap>)

      return { baseProductIds, providerProductIds }
    },
    []
  )

  const saveProviderTemplate = useCallback(
    async (
      formModel: FormModel,
      selectedProvider: RedemptionProvider
    ): Promise<boolean> => {
      const { typeItem, posFields } = formModel
      const { instoreRedemptionType, couponCodes, selectedCouponPools } =
        posFields[selectedProvider.value]
      if (!templateId || !typeItem || !instoreRedemptionType) {
        return true
      }
      const templateCouponPools = await getCouponPools(
        templateId,
        couponCodes.generationType
      )
      const providerCouponPools = templateCouponPools?.filter(
        pool => pool.redemption_provider?.value === selectedProvider.value
      )

      const templateType = getRedeemTemplateType(
        typeItem,
        instoreRedemptionType
      )
      const { subtype } = typeItem
      const params: Partial<UpdateParams> = {}

      params.coupon_pool_ids = []
      params.redemption_provider = selectedProvider.value
      if (instoreRedemptionType === 'manager_comp') {
        // if user selects manager_comp, we must clear
        // any assigned coupon pool and re-fetch template pools
        params.coupon_pool_ids = []
      } else if (
        couponCodes?.generationType === CouponPoolGenerationType.STATIC &&
        !!providerCouponPools?.length
      ) {
        params.coupon_pool_ids = [providerCouponPools[0].id]
      } else if (!!selectedCouponPools?.length) {
        let couponPoolIds: number[] = []
        couponPoolIds = selectedCouponPools.map(s => s.id)
        params.coupon_pool_ids = couponPoolIds
      }

      if (
        templateType === 'manual/item' ||
        templateType === 'manual/percent' ||
        templateType === 'manual/amount' ||
        templateType === 'manual/fixed_price' ||
        subtype === 'bogo/purchase'
      ) {
        const { baseProductIds, providerProductIds } = buildProductIds(
          formModel,
          templateType,
          subtype,
          posFields
        )
        params.product_ids = {
          ...baseProductIds,
          ...providerProductIds,
        }
      }

      const response = await dispatch(updateRedeemTemplate(templateId, params))
      if (!params.coupon_pool_ids?.length) {
        await getCouponPools(templateId)
      }
      if (response?.error) {
        dispatch(
          alert({
            key: 'danger',
            //@ts-ignore
            message: response?.error?.response?.data?.errors?.messages,
            timeout: 5,
          })
        )
        setIsSaving(false)
        return false
      }
      setIsSaving(false)
      return true
    },
    [templateId, getCouponPools, dispatch, buildProductIds]
  )

  const saveTemplate = useCallback(
    async (
      formModel: FormModel,
      selectedProvider?: RedemptionProvider
    ): Promise<boolean> => {
      // TODO: bailout if nothing's changed
      const {
        typeItem,
        name,
        instoreRedemptionType,
        description,
        discount,
        priceCap,
        perceivedValue,
        availableOnline,
        availableInstore,
        availableCustomerSupport,
        discountedProductIds,
        requiredProductIds,
        hiddenMenuKey,
        url,
        longDescription,
        numberOfPoints,
        selectedCouponPools,
        posFields,
        minimum,
        maximum: maximumInput,
      } = formModel

      if (!templateId || !typeItem) {
        return true
      }

      if (selectedProvider) return true

      const templateCouponPools = await getCouponPools(
        templateId,
        formModel?.couponCodes?.generationType
      )

      let redemptionVenue: RedemptionVenue = 'all'
      if (typeItem?.key === 'golden_ticket') {
        redemptionVenue = 'instore'
      } else if (!availableOnline) {
        redemptionVenue = 'instore'
      } else if (!availableInstore) {
        redemptionVenue = 'online'
      }
      const { key, subtype } = typeItem
      const isDeliveryDiscount = key === 'delivery_discount'

      const deliveryTemplateType = (
        mode: FormModel['deliveryFeeMode']
      ): RedeemType => {
        if (mode === 'percent') return 'manual/percent'
        if (mode === 'fixed_price') return 'manual/fixed_price'
        return 'manual/amount'
      }

      const templateType = isDeliveryDiscount
        ? deliveryTemplateType(formModel.deliveryFeeMode)
        : getRedeemTemplateType(typeItem, instoreRedemptionType)

      const isHiddenMenu = key === 'hidden_menu'
      const isExperience = isHiddenMenu || key === 'golden_ticket'
      const isBonusPoints = key === 'bonus_points'
      // $-off templates have no `maximum` field; never send one.
      const isDollarOff = templateType.endsWith('/amount')
      const supportsMinMax = !isExperience && !isBonusPoints

      const parseMoney = (v: string | null | undefined) => {
        if (v === null || v === undefined) return null
        const trimmed = String(v).trim()
        if (!trimmed) return null
        const n = Number(trimmed)
        return Number.isFinite(n) ? n : null
      }
      const parsedMinimum = parseMoney(minimum)
      const parsedMaximumInput = parseMoney(maximumInput)

      // null (Disable) → 0 to clear; '' (enabled, empty) → undefined to omit
      // and preserve the saved value; numeric → verbatim.
      const resolveMoneyWireValue = (
        modelValue: string | null | undefined,
        parsed: number | null
      ): number | undefined => {
        if (modelValue === null) return 0
        if (parsed === null) return undefined
        return parsed
      }

      let maximum: number | null = parsedMaximumInput
      if (
        !templateMinMaxEnabled &&
        maximum === null &&
        templateType === 'automatic/percent' &&
        (subtype === 'purchase' || subtype === 'bogo/purchase')
      ) {
        maximum = 250
      }

      const allowCouponPools = !isNDR(templateType)

      const parsedPriceCap = (() => {
        const trimmed = priceCap?.trim()
        if (!trimmed) return undefined
        const num = Number(trimmed)
        if (!Number.isFinite(num)) return undefined
        return num
      })()

      const params: Partial<RedeemTemplate> = {
        type: templateType,
        subtype,
        name,
        description,
        discount: Number(discount) || undefined,
        price_cap: parsedPriceCap,
        discount_scope: (() => {
          if (templateType !== 'manual/fixed_price') return undefined
          return isDeliveryDiscount ? 'delivery_fee' : formModel.discountScope
        })(),
        discounted_check_section:
          isDeliveryDiscount &&
          (templateType === 'manual/amount' ||
            templateType === 'manual/percent')
            ? 'delivery_fee'
            : undefined,
        perceived_value: Number(perceivedValue) || undefined,
        redemption_venue: isDeliveryDiscount ? undefined : redemptionVenue,
        granted_program_visible: availableCustomerSupport && !isExperience,
        discounted_product_ids:
          !isDeliveryDiscount && discountedProductIds?.length
            ? discountedProductIds
            : undefined,
        required_product_ids:
          !isDeliveryDiscount && requiredProductIds?.length
            ? requiredProductIds
            : undefined,
        // Flag off retains the legacy auto-cashback maximum.
        maximum:
          templateMinMaxEnabled && supportsMinMax && !isDollarOff
            ? resolveMoneyWireValue(maximumInput, parsedMaximumInput)
            : maximum ?? undefined,
        minimum:
          (isDeliveryDiscount || templateMinMaxEnabled) && supportsMinMax
            ? resolveMoneyWireValue(minimum, parsedMinimum)
            : undefined,
        url: url ?? undefined,
        long_description: longDescription,
      }

      if (allowCouponPools) {
        params.coupon_pool_ids = []
        if (instoreRedemptionType === 'manager_comp') {
          // if user selects manager_comp, we must clear
          // any assigned coupon pool and re-fetch template pools
          params.coupon_pool_ids = []
        } else if (
          formModel?.couponCodes?.generationType ===
            CouponPoolGenerationType.STATIC &&
          !!templateCouponPools?.length
        ) {
          params.coupon_pool_ids = [templateCouponPools[0].id]
        } else if (!!selectedCouponPools?.length) {
          let couponPoolIds: number[] = []
          couponPoolIds = selectedCouponPools.map(s => s.id)
          params.coupon_pool_ids = couponPoolIds
        }
      }
      const templateStaticPools = await getCouponPools(
        templateId,
        CouponPoolGenerationType.STATIC
      )
      const providerCouponPoolIds: number[] = Object.keys(posFields).reduce(
        (acc: number[], providerKey) => {
          const selectedPoolIds =
            posFields[providerKey].couponCodes.generationType !==
            CouponPoolGenerationType.STATIC
              ? posFields[providerKey].selectedCouponPools.map(s => s.id)
              : templateStaticPools?.find(
                  pool => pool.redemption_provider?.value === providerKey
                )?.id ?? []
          return acc.concat(selectedPoolIds)
        },
        []
      )
      params.coupon_pool_ids?.push(...providerCouponPoolIds)

      // update product_ids only for manual redemptions
      if (
        !isDeliveryDiscount &&
        (templateType === 'manual/item' ||
          templateType === 'manual/percent' ||
          templateType === 'manual/amount' ||
          templateType === 'manual/fixed_price' ||
          subtype === 'bogo/purchase')
      ) {
        const { baseProductIds, providerProductIds } = buildProductIds(
          formModel,
          templateType,
          subtype,
          posFields
        )
        params.product_ids = {
          ...baseProductIds,
          ...providerProductIds,
        }
      }

      if (isExperience) params.granted_program_visible = false
      if (isHiddenMenu) {
        const hiddenMenu = hiddenMenuKey
          ? categories.find(hm => hm.ids.join(',') === hiddenMenuKey[0])
          : null
        if (hiddenMenu) {
          params.unhides_category_ids = hiddenMenu.ids
          params.category_name = hiddenMenu.name
        } else {
          if (typeof hiddenMenuKey === 'string') {
            params.unhides_category_ids = hiddenMenuKey
              .split(',')
              .map(key => Number(key))
          }
        }
      }
      if (isBonusPoints && numberOfPoints) {
        params.number_of_points = Number(numberOfPoints)
        params.points_experience_id = pointsExperience?.id
      }

      if (
        isBonusPoints &&
        formModel.multiUseRewards.usageType === 'multiple_times' &&
        formModel.multiUseRewards.limitType === 'unlimited'
      ) {
        // Bonus points don't support unlimited multi-use — bail out entirely.
        return true
      } else if (formModel.multiUseRewards.usageType === 'multiple_times') {
        if (!formModel.multiUseRewards.activeWindowDuration) {
          return true
        } else if (
          formModel.multiUseRewards.limitType === 'unlimited' &&
          !formModel.multiUseRewards.maxIssuances
        ) {
          return true
        } else if (
          formModel.multiUseRewards.limitType === 'set_number' &&
          (!formModel.multiUseRewards.maxIssuancesPerWindow ||
            !formModel.multiUseRewards.windowDuration)
        ) {
          return true
        }

        params.multi_use_settings = {
          schema: 'Program::Issuance::MultiUseStrategies::EveryXDaysStrategy',
          end_condition: {
            max_issuances: formModel.multiUseRewards.maxIssuances
              ? Number(formModel.multiUseRewards.maxIssuances)
              : undefined,
            active_window_duration:
              Number(formModel.multiUseRewards.activeWindowDuration) *
              24 *
              60 *
              60,
          },
          limit_condition:
            formModel.multiUseRewards.limitType === 'set_number' &&
            formModel.multiUseRewards.maxIssuancesPerWindow &&
            formModel.multiUseRewards.windowDuration
              ? {
                  max_issuances_per_window: Number(
                    formModel.multiUseRewards.maxIssuancesPerWindow
                  ),
                  window_duration:
                    Number(formModel.multiUseRewards.windowDuration) *
                    24 *
                    60 *
                    60,
                }
              : undefined,
        }
      } else {
        params.multi_use_settings = null
      }

      const response = await dispatch(updateRedeemTemplate(templateId, params))
      if (!params.coupon_pool_ids?.length) {
        await getCouponPools(templateId)
      }

      if (response?.error) {
        dispatch(
          alert({
            key: 'danger',
            //@ts-ignore
            message: response?.error?.response?.data?.errors?.messages,
            timeout: 5,
          })
        )
        setIsSaving(false)
        return false
      }
      setIsSaving(false)
      return true
    },
    [
      buildProductIds,
      categories,
      templateId,
      dispatch,
      pointsExperience,
      getCouponPools,
      templateMinMaxEnabled,
    ]
  )

  const saveCouponPool = useCallback(
    async (
      formModel: FormModel,
      selectedProvider?: RedemptionProvider
    ): Promise<void> => {
      if (!templateId) return
      if (selectedProvider) {
        await saveProviderTemplate(formModel, selectedProvider)
        return
      }
      let couponPool: CouponPool | undefined
      const {
        generationType: formGenerationType,
        type,
        staticCode,
      } = formModel.couponCodes
      const generationType =
        formGenerationType ?? CouponPoolGenerationType.DYNAMIC
      const templateCouponPools = await getCouponPools(templateId)
      const couponPools =
        templateCouponPools?.filter(
          c => c.generation_type === generationType
        ) || []

      // We save the values only for static and dynamic generation types
      // for variable we'll always create a new CP for each upload.
      if (generationType === CouponPoolGenerationType.VARIABLE) {
        return
      }

      if (
        formModel.instoreRedemptionType === 'coupon_codes' &&
        !couponPools?.length &&
        !isEmpty(staticCode)
      ) {
        // in case we don't have a pool selected in a static code
        // and we have some code entered, we should create
        // a coupon pool which will be used from now on.
        couponPool = await createCouponPool({
          templateId,
          staticCode,
          generationType,
        })
      } else if (!!couponPools?.length) {
        couponPool = couponPools[0]
      }

      if (!couponPool) return

      // TC-3818: couponPool.static_code can take empty string or null values.
      // So we may skip updating the coupon pool when both the form and backend
      // has an empty static_code.
      // const isEmptyCodes = isEmpty(staticCode) && isEmpty(couponPool.static_code)
      const isSameStaticCode =
        staticCode === couponPool.static_code || isEmpty(staticCode)
      // Bail out if there's nothing to update
      if (
        generationType === couponPool.generation_type &&
        type === couponPool.type &&
        isSameStaticCode
      ) {
        return
      }

      const payload = {
        generation_type: generationType,
        type: type,
        static_code: staticCode,
      }
      await dispatch(updateCouponPoolAction(templateId, couponPool.id, payload))
      await getCouponPools(templateId)
    },
    [
      templateId,
      dispatch,
      getCouponPools,
      createCouponPool,
      saveProviderTemplate,
    ]
  )

  // A loyalty-integration provider's dynamic pool is otherwise only created when
  // its Configure POS screen mounts, which a merchant can skip. Other providers
  // create their pool through a different flow, so they're left alone here.
  // returnExistingPool makes this a no-op when the pool already exists.
  const ensureProviderCouponPools = useCallback(
    async (formModel: FormModel): Promise<void> => {
      if (!templateId) return
      for (const provider of redemptionProviders) {
        const posField = formModel.posFields[provider.value]
        if (
          posField?.instoreRedemptionType ===
            InstoreRedemptionType.COUPON_CODES &&
          isLoyaltyIntegrationPos(provider)
        ) {
          await createCouponPool({
            templateId,
            generationType: CouponPoolGenerationType.DYNAMIC,
            type: couponPoolType,
            returnExistingPool: true,
            redemptionProvider: provider,
          })
        }
      }
    },
    [templateId, redemptionProviders, couponPoolType, createCouponPool]
  )

  const save = useCallback(
    async (
      formModel: FormModel,
      selectedProvider?: RedemptionProvider
    ): Promise<boolean> => {
      setIsSaving(true)
      try {
        // we must save the template first, so we're confident about
        // assigned / unassigned pools before saving the coupon pool
        const success = await saveTemplate(formModel, selectedProvider)
        if (!success) return false
        await saveCouponPool(formModel, selectedProvider)
        // Per-provider saves create their own pool; back-fill skipped providers.
        if (!selectedProvider) {
          await ensureProviderCouponPools(formModel)
        }
        return true
      } finally {
        setIsSaving(false)
      }
    },
    [saveTemplate, saveCouponPool, ensureProviderCouponPools]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerSave = useCallback(debounce(save, 1000), [save])

  interface UpdateParams extends RedeemTemplate {
    redemption_provider: string
  }

  return {
    triggerSave,
    forceSave: save,
    isSaving,
    error: null,
  }
}
